import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
} from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { ChatContextService, ChatState } from "../../../../agent-ui";
import { MapContextBuilderService } from "../../services/map-context-builder.service";
import { AgentKitRuntimeService } from "../../services/agent-kit-runtime.service";
import { DebugStreamService } from "../../services/debug-stream.service";
import { ToolDataStreamService } from "../../services/tool-data-stream.service";
import { WorkerReasoningStreamService } from "../../services/worker-reasoning-stream.service";
import { WorkerTodoStreamService } from "../../services/worker-todo-stream.service";
import { BlackboardStreamService } from "../../services/blackboard-stream.service";
import { ContentBlockStreamService } from "../../services/content-block-stream.service";
import { EvidenceProgressStore } from "../../services/evidence-progress-store.service";
import { PlanBlockStoreService } from "../../services/plan-block-store.service";
import { ActionService } from "../../services/action.service";
import { ViewerHandoffService } from "../../services/viewer-handoff.service";
import { EvidenceLayerService } from "../../services/evidence-layer.service";
import { isTodoUpdateEvent, isSubAgentToolCallEvent } from "../../model/block.types";
import { AttachmentStateService } from "../../services/attachment-state.service";
import { HitlResumePayload, InterruptPayload } from "../../model/interrupt.types";
import { HitlService } from "../../services/hitl.service";
import type { Message } from "@ag-ui/client";
import { ChatViewComponent } from "../chat-view/chat-view.component";
import { randomUUID } from "@copilotkit/shared";
import { MapFrontendToolsService } from "../../services/map-frontend-tools.service";
import { MapLayerBlockService } from "../../services/map-layer-block.service";
import { ChatCore } from "../chat-core/chat-core";
import type { AgentIdleState } from "../../model/agent.types";
import { ChatIdleConfigBridge } from "../chat-idle-state/chat-idle-config.bridge";
import { CHAT_IDLE_DEFAULTS } from "../chat-idle-state/chat-idle-config.token";

/**
 * Smart container for the custom Material chat UI.
 * Extends ChatCore to inherit CopilotKit stream and state management.
 * Provides ChatState so ChatInputComponent can call submitInput/changeInput via DI.
 *
 * MapFrontendToolsService is provided here (not root) to avoid the NG0200 circular
 * dependency that arises when it is root-scoped. It is injected eagerly so its
 * constructor runs on first chat open and registers all map frontend tools via
 * AgentKitService.addFrontendTool() WITHOUT DestroyRef cleanup. This means tools
 * persist in #clientToolCallRenderConfigs for the entire app lifetime, and
 * subsequent registrations on panel re-open are no-ops (duplicate name check).
 */
@Component({
  selector: "lib-chat-container",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ChatViewComponent],
  templateUrl: "./chat-container.component.html",
  styleUrl: "./chat-container.component.scss",
  providers: [
    ChatContextService,
    AttachmentStateService,
    MapContextBuilderService,
    MapFrontendToolsService,
    // map-layer-block-ui S11: scoped like MapFrontendToolsService — the VIEWER_SERVICE probe and
    // the sibling ActionService add_layer routing both resolve from this container's injector.
    MapLayerBlockService,
    // HitlService is scoped here so each container instance owns its own HITL state.
    // When the container is destroyed (e.g. new thread via @for key), HITL resets automatically.
    // DebugMockPanelComponent overrides this with NoopHitlService for its mock cards.
    HitlService,
    // Scoped per container so sub-agent reasoning steps reset with the container
    // (new thread via @for key) and can be cleared per turn on submit.
    WorkerReasoningStreamService,
    // Same lifecycle as WorkerReasoningStreamService — holds the current turn's live
    // *_todo_update checklist for a sub-agent running outside any background_job.
    WorkerTodoStreamService,
    // Same lifecycle — holds the current turn's live planning-pipeline progress from
    // blackboard_snapshot CUSTOM events.
    BlackboardStreamService,
    // Same lifecycle — reduces STATE_SNAPSHOT events into per-message accumulated content
    // blocks (grow-only merge) and feeds the plan store + background-job tracker. This is the
    // NG0103 origin fix (chat-snapshot-stream-reducer): the snapshot reduction runs in this
    // stream callback — outside change detection — NEVER in a component effect.
    ContentBlockStreamService,
    // Same lifecycle — reduces the ephemeral evidence_progress CUSTOM events (vexcel
    // tool-execution traceability) into per-call brackets for the debug-only evidence card;
    // reacts to the plan_run terminal steps from the same container-scoped blackboard stream.
    EvidenceProgressStore,
    // Same lifecycle — holds the last-known raw Plan blocks (doc 31) keyed by plan_ref
    // (last wins) so the live task board can join task intents by plan_id (§7.5).
    PlanBlockStoreService,
    // Evidence-layer add flow (evidence-layer-ui US4/US5): scoped HERE — next to
    // PlanBlockStoreService — so `EvidenceLayerService` resolves the container's plan store and
    // group display names come from the plan's `display_name ?? request_objective` instead
    // of the key-derived fallback
    // fallback. `ActionService` is re-scoped for the same reason: it injects
    // `EvidenceLayerService`, and the root-scoped instance would bind to the store-less root
    // `EvidenceLayerService` (both are stateless, so re-scoping is behavior-neutral otherwise).
    EvidenceLayerService,
    ActionService,
    // Viewer-handoff (viewer-handoff plan F1.5): same re-scoping reason as ActionService —
    // it injects SearchQueryService, which consuming apps scope ABOVE this container
    // (main-layout precedent); the root instance would miss that scope (NG0200/NG0201).
    ViewerHandoffService,
    { provide: ChatState, useExisting: ChatContainerComponent },
    // Scoped bridge so ChatIdleStateComponent can reactively read the active agent's idle config.
    ChatIdleConfigBridge,
  ],
})
export class ChatContainerComponent extends ChatCore implements OnInit, OnDestroy {
  /** Emits the latest run error message (or null when cleared). */
  readonly runErrorChange = output<string | null>();

  /** Optional idle-state config sourced from the active AgentDefinition. */
  readonly idleConfig = input<AgentIdleState | undefined>(undefined);

  private readonly mapContextBuilder = inject(MapContextBuilderService);

  private readonly chatContext = inject(ChatContextService);

  private readonly debugService = inject(DebugStreamService);

  private readonly toolDataStream = inject(ToolDataStreamService);

  private readonly workerReasoning = inject(WorkerReasoningStreamService);

  private readonly workerTodo = inject(WorkerTodoStreamService);

  private readonly blackboard = inject(BlackboardStreamService);

  // Same container lifecycle as the blackboard stream — reduces STATE_SNAPSHOT events into
  // per-message accumulated content blocks (the NG0103 origin fix: reduction lives in the
  // stream layer, not in ContentDispatcherComponent's effect).
  private readonly contentBlockStream = inject(ContentBlockStreamService);

  // Same container lifecycle as the blackboard stream — reduces evidence_progress events
  // into per-call brackets for the debug-only evidence card family.
  private readonly evidenceProgress = inject(EvidenceProgressStore);

  private readonly planBlockStore = inject(PlanBlockStoreService);

  private readonly attachmentState = inject(AttachmentStateService);

  private readonly hitlService = inject(HitlService);

  // Ensures AgentKitRuntimeService is created (syncs auth headers from ViewerConfiguration).
  private readonly _runtime = inject(AgentKitRuntimeService);

  // Eagerly creates MapFrontendToolsService so its constructor runs immediately and
  // registers all frontend tools before the first agent run.
  private readonly _mapFrontendTools = inject(MapFrontendToolsService);

  private readonly destroy$ = new Subject<void>();

  private pendingInterrupt: InterruptPayload | null = null;

  private readonly idleConfigBridge = inject(ChatIdleConfigBridge);

  protected readonly runError = computed(() => this.agentStore().runError());

  /** Messages for the view — read directly from the persistent AgentStore. */
  protected messages = computed(() => this.agentStore().messages());

  constructor() {
    super();

    // Keep the idle-state bridge in sync when the active agent changes (e.g. user switches agents).
    effect(() => {
      this.idleConfigBridge.config.set({ ...CHAT_IDLE_DEFAULTS, ...this.idleConfig() });
    });

    // Forward run errors to the parent entry point (bubble/toolbar shows the banner).
    effect(() => {
      this.runErrorChange.emit(this.runError());
    });

    // Register with the debug service as soon as agentId is available.
    // Must not wait for store.agent — the debug dialog can open before the first run.
    effect(() => {
      const id = this.agentId();
      if (id) {
        this.debugService.register(this.agentKitService, id, this.threadId() ?? this.generatedThreadId);
      }
    });

    // Subscribe to the agent's custom event stream (tool_data_stream SSE events).
    effect((onCleanup) => {
      const store = this.agentStore();
      if (!store?.agent) return;

      this.debugService.hookAgent(store.agent);

      const subscription = store.agent.subscribe({
        onCustomEvent: ({ event }) => {
          if (event.name === "tool_data_stream") {
            this.toolDataStream.store(event.value as any);
          } else if (event.name === "reasoning") {
            this.workerReasoning.store(event.value as any);
          } else if (isTodoUpdateEvent(event.name)) {
            this.workerTodo.store(event.value as any, event.name);
          } else if (isSubAgentToolCallEvent(event.name)) {
            this.workerTodo.storeToolCall(event.value as any, event.name);
          } else if (event.name === "blackboard_snapshot") {
            this.blackboard.store(event.value as any);
          } else if (event.name === "evidence_progress") {
            // Best-effort ephemeral traceability — the store accepts the full Custom envelope
            // or a bare 9-key payload and shape-guards everything (never throws).
            this.evidenceProgress.store(event);
          } else if (event.name === "on_interrupt") {
            try {
              this.pendingInterrupt = JSON.parse(event.value as string) as InterruptPayload;
            } catch {
              console.error("ChatContainerComponent: failed to parse on_interrupt payload", event.value);
            }
          }
        },
        // Raw STATE_SNAPSHOT events reduce in the stream layer (chat-snapshot-stream-reducer
        // S6): rxjs callback → ContentBlockStreamService store → computeds. Never a component
        // effect pipeline.
        onStateSnapshotEvent: ({ event }) => this.contentBlockStream.store(event),
      });
      onCleanup(() => {
        subscription.unsubscribe();
        this.pendingInterrupt = null;
      });
    });

    effect(() => {
      this.attachmentState.setThreadId(this.threadId() ?? "");
    });

    // Propagate agent identity to ChatContextService so container-scoped services (e.g.
    // ContentBlockStreamService's background-job completion callback) can trigger a
    // parent-thread refresh when a background job completes.
    effect(() => {
      this.chatContext.agentId.set(this.resolvedAgentId());
      this.chatContext.threadId.set(this.threadId() ?? this.generatedThreadId);
    });

    // WorkerReasoningStreamService/HitlService reset when the container is destroyed (new
    // thread via @for key), but host templates only key on threadId — switching to a
    // different agent without a thread change reuses this same container/service instance.
    // Clear directly on agentId transitions so stale in-flight steps or a stale HITL
    // wizard/progress state never bleed into the new agent's view, regardless of how the
    // host wires the container.
    let lastAgentId = this.resolvedAgentId();
    effect(() => {
      const agentId = this.resolvedAgentId();
      if (agentId !== lastAgentId) {
        lastAgentId = agentId;
        this.workerReasoning.clear();
        this.workerTodo.clear();
        this.blackboard.clear();
        this.contentBlockStream.clear();
        // Same lifecycle as the streams — stale plan blocks must not bleed into the
        // new agent's view. NOT cleared on submit: plan blocks are durable per message
        // (doc 31 §3) and are re-fed by the content dispatcher.
        this.planBlockStore.clear();
        this.hitlService.clear();
      }
    });
  }

  ngOnInit(): void {
    this.syncMapContextWithAI();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Checks the agent state after a run completes and dispatches any interrupt.
   * Called after every runAgent await — this is the single place where interrupts
   * are detected, guaranteeing the stream is always finished before we act on one.
   */
  private async handleInterruptIfPresent(): Promise<void> {
    const payload = this.pendingInterrupt;
    this.pendingInterrupt = null;

    if (!payload || !("type" in payload)) {
      this.hitlService.clear();
      return;
    }

    this.hitlService.activate(payload);

    if (payload.type === "frontend_tools") {
      await this.executeAndResumeFrontendTools(payload);
    }
  }

  /**
   * Re-runs the agent with the existing message history (no new user message).
   * Called when the user clicks "Retry" on the error banner.
   */
  async retryLastRun(): Promise<void> {
    const agent = this.agentStore().agent;
    if (!agent) return;

    this.showCursor.set(true);
    this.pendingInterrupt = null;
    try {
      await this.agentKitService.core.runAgent({ agent });
      await this.handleInterruptIfPresent();
    } catch (error) {
      console.error("ChatContainerComponent: retry error:", error);
    } finally {
      this.showCursor.set(false);
    }
  }

  /**
   * Resumes a LangGraph interrupt by sending all collected clarification answers.
   * The payload is the array of { id, value } items built by HitlService.
   */
  async resumeWithAnswer(payload: HitlResumePayload): Promise<void> {
    const agent = this.agentStore().agent;
    if (!agent || !payload.length) return;

    this.hitlService.resolve();
    this.showCursor.set(true);
    this.pendingInterrupt = null;

    try {
      await this.agentKitService.core.runAgent({
        agent,
        forwardedProps: { command: { resume: payload } },
      });
      await this.handleInterruptIfPresent();
    } catch (error) {
      console.error("ChatContainerComponent: HITL resume error:", error);
    } finally {
      this.showCursor.set(false);
    }
  }

  /**
   * Executes all tool_call items in the payload and resumes the agent with results.
   * The tool-call card stays visible (frontendToolsPayload is non-null) for the
   * entire duration. Called only from handleInterruptIfPresent, after a run ends.
   */
  private async executeAndResumeFrontendTools(payload: InterruptPayload): Promise<void> {
    const agent = this.agentStore().agent;
    const toolItems = payload.items.filter((i) => i.kind === "tool_call");
    const minVisible = new Promise<void>((resolve) => setTimeout(resolve, 300));
    const [responses] = await Promise.all([
      Promise.all(
        toolItems.map((item) =>
          this.agentKitService
            .callFrontendTool(item.tool_name, item.tool_args, agent)
            .then((result): HitlResumePayload[number] => {
              return { id: item.id, value: JSON.stringify(result) };
            })
            .catch((error): HitlResumePayload[number] => {
              console.error(`ChatContainerComponent: frontend tool "${item.tool_name}" failed:`, error);
              return { id: item.id, value: JSON.stringify({ error: String(error) }) };
            }),
        ),
      ),
      minVisible,
    ]);
    this.hitlService.clearFrontendTools();
    await this.resumeWithAnswer(responses);
  }

  /**
   * Overrides AgentChat.submitInput to handle attachments and detect interrupts after the run.
   */
  override async submitInput(value: string): Promise<void> {
    const agent = this.agentStore().agent;
    if (!agent || !value.trim()) return;

    const readyAttachments = this.attachmentState.files().filter((f) => f.status === "ready" && f.contentBlock);

    if (readyAttachments.length === 0) {
      agent.addMessage({ id: randomUUID(), role: "user", content: value } as Message);
    } else {
      const content: unknown[] = [];
      if (value.trim()) content.push({ type: "text", text: value.trim() });
      content.push(...readyAttachments.map((a) => a.contentBlock!));
      agent.addMessage({ id: randomUUID(), role: "user", content } as unknown as Message);
      this.attachmentState.clear();
    }

    this.changeInput("");
    this.showCursor.set(true);
    this.pendingInterrupt = null;
    // New turn: drop the previous turn's sub-agent reasoning steps/todos. Cleared here
    // (not via a threadId effect, which races with the mid-run threadId transition).
    this.workerReasoning.clear();
    this.workerTodo.clear();
    this.blackboard.clear();

    try {
      await this.agentKitService.core.runAgent({ agent });
      await this.handleInterruptIfPresent();
    } catch (error) {
      console.error("ChatContainerComponent: submitInput error:", error);
    } finally {
      this.showCursor.set(false);
    }
  }

  private syncMapContextWithAI(): void {
    this.mapContextBuilder.context$.pipe(takeUntil(this.destroy$)).subscribe((ctx) => {
      this.agentKitService.updateMapContext(ctx);
      this.debugService.updateContext(ctx);
    });
  }
}
